import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, FloppyDisk, Plus, Trash, MapTrifold, ArrowsClockwise, CaretRight, CaretDown, Calculator } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

const n = (v) => (v === "" || v == null ? null : Number(String(v).replace(",", ".")));
const toForm = (r) => ({
  city: r.city, district: r.district || "", zip: (r.area?.zip_codes || r.zip_codes || []).join(", "),
  low: r.area?.low ?? "", mid: r.area?.mid ?? "", high: r.area?.high ?? "", source: r.area?.source || "",
  source_ref: r.area?.source_ref || "", origin: r.area?.origin || "manual", id: r.area?.id, tenancies: r.tenancies, saved: !!r.area,
  tight: !!r.tight_market, auto: !!r.auto,
});

function AreaRow({ f, onChange, onSave, onSuggest, onBestand, onDelete, onTight, canEdit, i, cityRow, expanded, onToggle, districtCount }) {
  const set = (k) => (e) => onChange({ ...f, [k]: e.target.value, ...(["low", "mid", "high"].includes(k) ? { origin: "manual" } : {}) });
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`area-row-${i}`}>
      <td className={`py-1.5 pr-2 ${f.district ? "pl-8" : ""}`}>
        {f.district ? <span className="text-sm">{f.district}</span> : (
          <button type="button" onClick={onToggle} className="flex items-center gap-1 text-sm font-semibold text-slate-900" data-testid={`area-city-toggle-${i}`}>
            {expanded ? <CaretDown size={13} /> : <CaretRight size={13} />}{f.city}
            <span className="text-[10px] font-normal text-slate-500 ml-1">{districtCount} Stadtteil(e)</span>
          </button>)}
        <div className="text-[10px] text-slate-500">{f.tenancies} Mietverhältnis(se){f.saved ? "" : (f.district ? (cityRow?.mid ? ` · erbt Stadtwert ${cityRow.mid} €/m²` : " · erbt Stadtwert") : " · nicht gespeichert")}{f.auto && <span className="text-teal-700"> · automatisch erkannt</span>}</div></td>
      <td className="pr-2">{f.district ? <span className="text-[11px] text-slate-400">wie Stadt</span> : (
        <label className="flex items-center gap-1.5 text-[11px] whitespace-nowrap cursor-pointer">
          <Checkbox checked={f.tight} disabled={!canEdit} onCheckedChange={(v) => onTight(!!v)} data-testid={`area-tight-${i}`} /> 15 %
        </label>)}</td>
      {["low", "mid", "high"].map((k) => <td key={k} className="pr-1"><Input className="h-8 w-20 text-xs tabular-nums" value={f[k]} onChange={set(k)} inputMode="decimal" placeholder={f.district && cityRow?.[k] ? String(cityRow[k]) : ""} data-testid={`area-${k}-${i}`} /></td>)}
      <td className="pr-1"><Input className="h-8 text-xs w-48" value={f.source} onChange={set("source")} placeholder="Mietspiegel 2025" data-testid={`area-source-${i}`} />
        {f.origin === "miete-aktuell" && <div className="text-[10px] text-amber-700 mt-0.5">Domivus-Vorschlag</div>}
        {f.origin === "bestand" && <div className="text-[10px] text-indigo-700 mt-0.5">aus eigenem Bestand berechnet</div>}</td>
      <td className="pr-1"><Input className="h-8 text-xs w-24" value={f.source_ref} onChange={set("source_ref")} placeholder="Feld" data-testid={`area-ref-${i}`} /></td>
      <td className="whitespace-nowrap">
        <Button size="icon" variant="ghost" className="h-8 w-8" title={f.district ? "Domivus-Vorschlag für den Stadtteil" : "Domivus-Vorschlag für die Stadt"} onClick={onSuggest} data-testid={`area-suggest-${i}`}><Sparkle size={14} /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Aus eigenen Mietverhältnissen berechnen" onClick={onBestand} data-testid={`area-bestand-${i}`}><Calculator size={14} /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" title="Speichern" onClick={onSave} data-testid={`area-save-${i}`}><FloppyDisk size={14} /></Button>
        {f.id && <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" title="Löschen" onClick={onDelete}><Trash size={14} /></Button>}
      </td>
    </tr>
  );
}

export default function AreasStep() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nd, setNd] = useState({ city: "", district: "" });
  const [detect, setDetect] = useState(null);
  const { user } = useAuth();
  const canEdit = ["admin", "super_admin"].includes(user?.role);
  const load = () => api.get("/rent-increase/areas").then(({ data }) => { setRows(data.rows.map(toForm)); setDetect(data.detect || null); });
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!detect?.running) return undefined; const t = setTimeout(load, 4000); return () => clearTimeout(t); }, [detect]); // eslint-disable-line react-hooks/exhaustive-deps
  const redetect = async () => { await api.post("/rent-increase/areas/detect-districts", { force: true }); toast.success("Stadtteile werden neu erkannt …"); load(); };
  const setTight = async (i, v, fx) => {
    const f = fx || rows[i];
    try { await api.put("/rent-increase/areas/tight-market", { city: f.city, tight_market: v }); toast.success(`${f.city}: Kappungsgrenze ${v ? "15" : "20"} %`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const upd = (i, f) => setRows((r) => r.map((x, j) => (j === i ? f : x)));
  const suggest = async (i, quiet) => {
    const f = rows[i];
    try {
      const { data } = await api.get("/rent-increase/areas/suggestion", { params: { city: f.city, district: f.district || undefined } });
      upd(i, { ...f, low: data.low, mid: data.mid, high: data.high, source: data.source, source_ref: data.source_ref, origin: "miete-aktuell" });
      if (data.city_fallback && !quiet) toast.info(`${f.district}: kein Stadtteilwert gefunden – Wert der Stadt ${f.city} übernommen`);
      return true;
    } catch (e) { toast.error(`${f.city}${f.district ? ` ${f.district}` : ""}: ${e?.response?.data?.detail || "kein Vorschlag"}`); return false; }
  };
  const bestand = async (i) => {
    const f = rows[i];
    try {
      const { data } = await api.get("/rent-increase/areas/bestand", { params: { city: f.city, district: f.district || undefined } });
      upd(i, { ...f, low: data.low, mid: data.mid, high: data.high, source: data.source, source_ref: "", origin: "bestand" });
      toast.success(`${data.count} Mietverhältnis(se) ausgewertet – bitte prüfen und speichern`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Keine Daten"); }
  };
  const [open, setOpen] = useState({});
  const [maList, setMaList] = useState({});
  const toggle = async (city) => {
    setOpen((o) => ({ ...o, [city]: !o[city] }));
    if (!maList[city]) api.get("/rent-increase/areas/ma-districts", { params: { city } }).then(({ data }) => setMaList((m) => ({ ...m, [city]: data.districts }))).catch(() => setMaList((m) => ({ ...m, [city]: [] })));
  };
  const addTo = (city, district) => {
    if (!district || rows.some((r) => r.city === city && r.district.toLowerCase() === district.toLowerCase())) return;
    setRows((r) => [...r, toForm({ city, district, tenancies: 0 })]);
  };
  const save = async (f, quiet) => {
    if (!n(f.mid)) { if (!quiet) toast.error("Mittelwert fehlt"); return false; }
    try {
      await api.put("/rent-increase/areas", { city: f.city, district: f.district || null, zip_codes: f.zip.split(/[,;\s]+/).filter(Boolean),
        low: n(f.low), mid: n(f.mid), high: n(f.high), source: f.source || null, source_ref: f.source_ref || null, origin: f.origin === "bestand" ? "manual" : f.origin });
      if (!quiet) { toast.success("Gespeichert"); load(); }
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); return false; }
  };
  const suggestAll = async () => {
    setBusy(true);
    let ok = 0;
    for (let i = 0; i < rows.length; i += 1) if (await suggest(i, true)) ok += 1; // eslint-disable-line no-await-in-loop
    setBusy(false);
    toast.success(`${ok} Domivus-Vorschläge geladen – bitte prüfen und „Alle speichern“`);
  };
  const saveAll = async () => {
    setBusy(true);
    let c = 0;
    for (const f of rows) if (n(f.mid) && await save(f, true)) c += 1; // eslint-disable-line no-await-in-loop
    setBusy(false); toast.success(`${c} Wert(e) gespeichert`); load();
  };
  const addDistrict = () => {
    const c = nd.city.trim();
    if (!c || rows.some((r) => r.city.toLowerCase() === c.toLowerCase())) return;
    setRows((r) => [...r, toForm({ city: c, tenancies: 0 })]); setNd({ city: "", district: "" });
  };
  return (
    <div className="space-y-3" data-testid="areas-step">
      <p className="text-sm text-slate-600">Allgemeiner Mietspiegel je Stadt (€/m² netto kalt). Klick auf eine Stadt zeigt die Stadtteile – ohne eigenen Wert erben sie den Stadtwert. Vorschläge: Stern = Domivus-Vorschlag (Marktdaten), Rechner = eigener Bestand. Stadtteile werden über den Namen zugeordnet, nicht über die PLZ. Kappungsgrenze 15 % je Stadt (angespannter Wohnungsmarkt) – maßgeblich für alle Prüfungen. Eigene Werte an Objekt/Einheit haben Vorrang.</p>
      {detect?.running && (
        <div className="flex items-center gap-2 rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900" data-testid="areas-detect-running">
          <MapTrifold size={14} className="animate-pulse" /> Stadtteile werden automatisch erkannt{detect.total ? ` (${detect.done}/${detect.total} Objekte)` : " …"}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="gap-1" onClick={suggestAll} disabled={busy} data-testid="areas-suggest-all"><Sparkle size={14} /> Domivus-Vorschläge für alle laden</Button>
        <Button size="sm" className="gap-1" onClick={saveAll} disabled={busy} data-testid="areas-save-all"><FloppyDisk size={14} /> Alle speichern</Button>
        <Button size="sm" variant="ghost" className="gap-1 ml-auto" onClick={redetect} disabled={detect?.running} data-testid="areas-redetect"><ArrowsClockwise size={14} /> Stadtteile neu erkennen</Button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-2 py-1.5">Stadt / Stadtteil</th><th title="Angespannter Wohnungsmarkt (Kappungsgrenzen-Verordnung)">Kappungsgrenze</th><th>unten</th><th>Mittel</th><th>oben</th><th>Quelle</th><th>Feld</th><th /></tr></thead>
          <tbody className="[&_td:first-child]:px-2">
            {[...new Set(rows.map((r) => r.city))].map((city) => {
              let ci = rows.findIndex((r) => r.city === city && !r.district);
              const dis = rows.map((r, i) => [r, i]).filter(([r]) => r.city === city && r.district);
              const cityF = ci >= 0 ? rows[ci] : toForm({ city, tenancies: dis.reduce((a, [r]) => a + (r.tenancies || 0), 0) });
              if (ci < 0) ci = `c-${city}`;
              const cityVals = { low: n(cityF.low), mid: n(cityF.mid), high: n(cityF.high) };
              const rowProps = (f, i) => ({ f, i, canEdit, cityRow: cityVals, onChange: (x) => (typeof i === "number" ? upd(i, x) : setRows((r) => [...r, x])),
                onSave: () => save(f), onSuggest: () => (typeof i === "number" ? suggest(i) : (setRows((r) => [...r, f]), toast.info("Stadtzeile angelegt – bitte erneut klicken"))),
                onBestand: () => (typeof i === "number" ? bestand(i) : setRows((r) => [...r, f])), onTight: (v) => setTight(i, v, f),
                onDelete: async () => { await api.delete(`/rent-increase/areas/${f.id}`); load(); } });
              const used = new Set(dis.map(([r]) => r.district.toLowerCase()));
              return [
                <AreaRow key={`city-${city}`} {...rowProps(cityF, ci)} expanded={!!open[city]} onToggle={() => toggle(city)} districtCount={dis.length} />,
                ...(open[city] ? dis.map(([f, i]) => <AreaRow key={`${city}-${f.district}-${f.id || i}`} {...rowProps(f, i)} />) : []),
                open[city] && (
                  <tr key={`add-${city}`} className="border-t border-slate-100"><td colSpan={10} className="pl-8 py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <select className="h-8 rounded border border-slate-300 text-xs px-2 max-w-xs" defaultValue="" onChange={(e) => { addTo(city, e.target.value); e.target.value = ""; }} data-testid={`area-ma-select-${city}`}>
                        <option value="">{maList[city] ? `Stadtteil hinzufügen (${maList[city].length} bekannt)` : "Lade Stadtteile…"}</option>
                        {(maList[city] || []).filter((x) => !used.has(x.toLowerCase())).map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                      <Input className="h-8 w-40 text-xs" placeholder="oder Stadtteil eingeben" onKeyDown={(e) => { if (e.key === "Enter") { addTo(city, e.target.value.trim()); e.target.value = ""; } }} data-testid={`area-add-input-${city}`} />
                    </div>
                  </td></tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2">
        <Input className="h-8 w-40 text-xs" placeholder="Stadt" value={nd.city} onChange={(e) => setNd({ ...nd, city: e.target.value })} data-testid="areas-new-city" />
        <Button size="sm" variant="outline" className="gap-1" onClick={addDistrict} data-testid="areas-add-city"><Plus size={13} /> Stadt hinzufügen</Button>
      </div>
    </div>
  );
}
